// ========================================
// pages/Landing.js - Luxury Landing Page
// ========================================
// LAST MODIFIED: April 11, 2026
// 
// NOTE: Header and footer are now provided by Layout component.
// This file ONLY contains the main content sections.
// 
// VIDEO ASSETS (from /public/videos/):
// - hero-video.mp4 - Looping background video for hero section
// - explainer-video.mp4 - How it works video (modal on "Watch Video" click)
// - testimonial1.mp4, testimonial2.mp4 - Customer testimonial videos
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const Landing = () => {
  const navigate = useNavigate();
  
  
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [unclaimedCount, setUnclaimedCount] = useState(0);
  const [showUnclaimedInfo, setShowUnclaimedInfo] = useState(false);
  const [loadingCount, setLoadingCount] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // ========================================
  // FETCH UNCLAIMED ASSETS COUNT
  // ========================================
  useEffect(() => {
    fetchUnclaimedCount();
  }, []);

  useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    navigate('/dashboard');
  }
}, [navigate]);

console.log('Landing component is MOUNTED and RENDERING');

  const fetchUnclaimedCount = async () => {
  const token = localStorage.getItem('token');
  if (token) {
    setLoadingCount(false);
    return;
  }
  
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/unclaimed/count`);
    setUnclaimedCount(response.data.count);
  } catch (error) {
    console.error('Failed to fetch unclaimed count:', error);
    setUnclaimedCount(0);
  } finally {
    setLoadingCount(false);
  }
};

  // ========================================
  // CHECK IF USER IS LOGGED IN
  // ========================================

  

  const displayCount = loadingCount ? '...' : unclaimedCount.toLocaleString();

  return (
    <div className="landing-container">
      
      {/* ======================================== */}
      {/* HERO SECTION WITH BACKGROUND VIDEO */}
      {/* ======================================== */}
      <section className="hero">
        {/* Background Video */}
        <video 
          className="hero-video-bg" 
          autoPlay 
          loop 
          muted 
          playsInline
          poster="/images/hero/hero-main.jpg"
        >
          <source src="/videos/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-badge">
              <img src="/images/symbols/golden-stool.png" alt="" />
              <span>Trusted by Ghanaian Families</span>
            </div>
            <h1 className="hero-title">
              Preserve Your Legacy for <span className="gold">Generations</span>
            </h1>
            <p className="hero-subtitle">
              Agyapadie Vault securely stores your digital assets, documents, 
              and inheritance instructions. Ensure your loved ones receive 
              what's rightfully theirs.
            </p>
            <div className="hero-buttons">
              <Link to="/register" className="btn-primary">Start Protecting Your Legacy</Link>
			   <Link to="/how-it-works" className="btn-how-it-works">How It Works</Link>
              <button 
                className="btn-secondary"
                onClick={() => setShowVideoModal(true)}
              >
                Watch Explainer Video
              </button>
            </div>
            <div className="hero-trust">
              <img src="/images/icons/feature-security.png" alt="" />
              <span>Bank-Grade Security • Cryptographic Proofs</span>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/hero/hero-main.jpg" alt="Ghanaian family preserving wealth" />
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* UNCLAIMED ASSETS COUNTER */}
      {/* ======================================== */}
      <section className="unclaimed">
        <div className="unclaimed-inner">
          <div className="unclaimed-card" onClick={() => setShowUnclaimedInfo(true)}>
            <img src="/images/icons/unclaimed-icon.png" alt="" />
            <div className="unclaimed-text">
              <span className="unclaimed-label">Unclaimed Assets in Our System</span>
              <span className="unclaimed-number">{displayCount}</span>
            </div>
            <img src="/images/icons/arrow-right-gold.png" alt="" className="unclaimed-arrow" />
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* FEATURES SECTION */}
      {/* ======================================== */}
      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">Why Choose <span className="gold">Agyapadie</span></h2>
          <div className="features-grid">
            <div className="feature-card">
              <img src="/images/icons/feature-security.png" alt="" />
              <h3>Bank-Grade Security</h3>
              <p>Your assets are encrypted and protected with cryptographic proofs.</p>
            </div>
            <div className="feature-card">
              <img src="/images/icons/feature-tradition.png" alt="" />
              <h3>Traditional Values</h3>
              <p>Designed for Ghanaian family structures and inheritance customs.</p>
            </div>
            <div className="feature-card">
              <img src="/images/icons/feature-fraud.png" alt="" />
              <h3>Proof Against Fraud</h3>
              <p>Cryptographic proofs protect your family from dishonest claims.</p>
            </div>
            <div className="feature-card">
              <img src="/images/icons/feature-trustees.png" alt="" />
              <h3>Multi-Trustee System</h3>
              <p>Multiple trustees verify death, ensuring no single point of failure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* HOW IT WORKS SECTION */}
      {/* ======================================== */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <h2 className="section-title">How <span className="gold">It Works</span></h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <img src="/images/hero/step1-create.jpg" alt="" />
              <h3>Create Account</h3>
              <p>Sign up and set up your secure vault</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <img src="/images/hero/step2-assets.jpg" alt="" />
              <h3>Add Assets</h3>
              <p>Store bank accounts, properties, and documents</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <img src="/images/hero/step3-trustees.jpg" alt="" />
              <h3>Appoint Trustees</h3>
              <p>Choose trusted people to verify your death</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <img src="/images/hero/step4-verify.jpg" alt="" />
              <h3>Death Verification</h3>
              <p>Trustees confirm, assets unlock automatically</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* PLANS SECTION - ONE-TIME PAYMENT */}
      {/* ======================================== */}
      <section id="plans" className="plans">
        <div className="container">
          <h2 className="section-title-white">Choose Your <span className="gold">Plan</span></h2>
          <p className="plans-subtitle">One-time payment • Lifetime access • No recurring fees</p>
          <div className="plans-grid">
            
            {/* Free Plan */}
            <div className="plan-card">
              <img src="/images/plans/plan-free.png" alt="Free" className="plan-icon" />
              <h3>Free</h3>
              <div className="plan-price">GHS 0 <span>one-time</span></div>
              <ul>
                <li>✓ 2 Global Next of Kin</li>
                <li>✓ Up to 10 Assets</li>
                <li>✓ 2 Trustees</li>
                <li>✗ No External Beneficiaries</li>
                <li>✓ 2MB file uploads</li>
              </ul>
              <Link to="/register" className="plan-btn">Get Started</Link>
            </div>
            
            {/* Premium Plan - Most Popular */}
            <div className="plan-card popular">
              <div className="popular-tag">MOST POPULAR</div>
              <img src="/images/plans/plan-premium.png" alt="Premium" className="plan-icon" />
              <h3>Premium</h3>
              <div className="plan-price">GHS 399 <span>one-time</span></div>
              <ul>
                <li>✓ 4 Global Next of Kin</li>
                <li>✓ Up to 50 Assets</li>
                <li>✓ Up to 3 Trustees</li>
                <li>✓ 1 External Beneficiary PER ASSET</li>
                <li>✓ 5MB file uploads</li>
                <li>✓ Choose 1 or 2 of 3 Verifiers</li>
              </ul>
              <Link to="/register" className="plan-btn gold">Choose Premium</Link>
            </div>
            
            {/* Business Plan */}
            <div className="plan-card">
              <img src="/images/plans/plan-business.png" alt="Business" className="plan-icon" />
              <h3>Business</h3>
              <div className="plan-price">GHS 999 <span>one-time</span></div>
              <ul>
                <li>✓ 6 Global Next of Kin</li>
                <li>✓ Up to 100 Assets</li>
                <li>✓ Up to 6 Trustees</li>
                <li>✓ 2 External Beneficiaries PER ASSET</li>
                <li>✓ 10MB file uploads</li>
                <li>✓ Choose 1,2, or 3 of 6 Verifiers</li>
                <li>✓ Priority email support</li>
              </ul>
              <Link to="/register" className="plan-btn">Choose Business</Link>
            </div>
            
            {/* Enterprise Plan */}
            <div className="plan-card">
              <img src="/images/plans/plan-enterprise.png" alt="Enterprise" className="plan-icon" />
              <h3>Enterprise</h3>
              <div className="plan-price">GHS 2,999 <span>one-time</span></div>
              <ul>
                <li>✓ 10 Global Next of Kin</li>
                <li>✓ Up to 400 Assets</li>
                <li>✓ Up to 10 Trustees</li>
                <li>✓ 3 External Beneficiaries PER ASSET</li>
                <li>✓ 20MB file uploads</li>
                <li>✓ Choose 1-4 of 10 Verifiers</li>
                <li>✓ Priority 24/7 support</li>
              </ul>
              <Link to="/contact" className="plan-btn">Contact Sales</Link>
            </div>
            
          </div>
          <p className="enterprise-note">
            💡 All plans are <strong>ONE-TIME PAYMENT</strong> - pay once, use forever.
          </p>
        </div>
      </section>

      {/* ======================================== */}
      {/* TESTIMONIALS SECTION WITH VIDEOS */}
      {/* ======================================== */}
      <section className="testimonials">
        <div className="container">
          <h2 className="section-title">Trusted by <span className="gold">Ghanaian Families</span></h2>
          <div className="testimonials-grid">
            
            {/* Testimonial 1 - Video */}
            <div className="testimonial-card video-card">
              <video 
                className="testimonial-video"
                controls
                playsInline
                poster="/images/hero/customer1.jpg"
              >
                <source src="/videos/testimonial1.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="testimonial-caption">
                <h4>Nana Appiah Mensah</h4>
                <p>Business Owner, Accra</p>
              </div>
            </div>
            
            {/* Testimonial 2 - Video */}
            <div className="testimonial-card video-card">
              <video 
                className="testimonial-video"
                controls
                playsInline
                poster="/images/hero/customer2.jpg"
              >
                <source src="/videos/testimonial2.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="testimonial-caption">
                <h4>Kwame Asante</h4>
                <p>Entrepreneur, Kumasi</p>
              </div>
            </div>
            
            {/* Testimonial 3 - Static with quote */}
            <div className="testimonial-card">
              <img src="/images/icons/quote-mark.png" alt="" className="quote" />
              <p>"Finally, a digital vault that respects our traditions while using modern technology."</p>
              <div className="author">
                <img src="/images/hero/customer3.jpg" alt="" />
                <div>
                  <h4>Adwoa Boateng</h4>
                  <p>Lawyer, Tema</p>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* FINAL CTA SECTION */}
      {/* ======================================== */}
      <section className="final-cta">
        <div className="container">
          <div className="final-cta-content">
            <h2>Start Preserving Your Legacy Today</h2>
            <p>It takes less than 5 minutes to secure your family's future.</p>
            <div className="final-cta-buttons">
              <Link to="/register" className="final-cta-primary">Sign Up Now - It's Free</Link>
              <Link to="/login" className="final-cta-secondary">Already have an account? Log In</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* EXPLAINER VIDEO MODAL */}
      {/* ======================================== */}
      {showVideoModal && (
        <div className="video-modal-overlay" onClick={() => setShowVideoModal(false)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setShowVideoModal(false)}>×</button>
            <video 
              className="explainer-video"
              controls
              autoPlay
              playsInline
            >
              <source src="/videos/explainer-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* UNCLAIMED ASSETS MODAL */}
      {/* ======================================== */}
      {showUnclaimedInfo && (
        <div className="modal-overlay" onClick={() => setShowUnclaimedInfo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUnclaimedInfo(false)}>×</button>
            <img src="/images/icons/unclaimed-icon.png" alt="" />
            <h2>🏦 Unclaimed Assets</h2>
            <p>There are currently <strong>{displayCount}</strong> unclaimed assets in the Agyapadie Vault system.</p>
            <p>These are assets whose owners have been marked deceased, but no beneficiary has claimed them yet.</p>
            <div className="modal-info">
              <p>📧 <strong>Check your email inbox.</strong> Beneficiaries receive a claim link.</p>
              <p>🏛️ For inquiries: <a href="mailto:claims@agyapadie.com">claims@agyapadie.com</a></p>
            </div>
            <button className="modal-btn" onClick={() => setShowUnclaimedInfo(false)}>Close</button>
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* STYLES - Including video styles */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Montserrat', sans-serif; background: white; }
        .gold { color: #FFD700; }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        
        /* Hero Section with Video Background */
        .hero {
          position: relative;
          background: #0A0A0A;
          overflow: hidden;
          min-height: 600px;
          display: flex;
          align-items: center;
        }
        
        .hero-video-bg {
          position: absolute;
          top: 50%;
          left: 50%;
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          transform: translate(-50%, -50%);
          object-fit: cover;
          z-index: 0;
        }
        
        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.6);
          z-index: 1;
        }
        
        .hero-inner {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          margin: 0 auto;
          padding: 80px 40px;
          display: flex;
          gap: 60px;
          align-items: center;
        }
        
		.btn-how-it-works {
  padding: 18px 32px;
  background: transparent;
  border: 2px solid #FFD700;
  color: #FFD700;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn-how-it-works:hover {
  background: rgba(255, 215, 0, 0.1);
  transform: translateY(-2px);
}
		
        .hero-content { flex: 1; }
        .hero-badge { display: inline-flex; align-items: center; gap: 10px; background: rgba(255,215,0,0.1); padding: 8px 20px; border-radius: 40px; margin-bottom: 24px; }
        .hero-badge img { height: 20px; }
        .hero-badge span { color: #FFD700; font-size: 14px; }
        .hero-title { font-size: 52px; font-weight: bold; color: white; line-height: 1.2; margin-bottom: 24px; }
        .hero-subtitle { font-size: 18px; color: #ccc; line-height: 1.6; margin-bottom: 32px; }
        .hero-buttons { display: flex; gap: 20px; margin-bottom: 32px; flex-wrap: wrap; }
        .btn-primary { padding: 14px 32px; background: #FFD700; color: #0A0A0A; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block; transition: all 0.3s; }
        .btn-primary:hover { background: #B8860B; }
        .btn-secondary { padding: 14px 32px; background: transparent; border: 2px solid #FFD700; color: #FFD700; border-radius: 5px; font-weight: bold; cursor: pointer; transition: all 0.3s; }
        .btn-secondary:hover { background: #FFD700; color: #0A0A0A; }
        .hero-trust { display: flex; align-items: center; gap: 12px; padding: 12px 20px; background: rgba(255,255,255,0.05); border-radius: 10px; width: fit-content; }
        .hero-trust img { height: 24px; }
        .hero-trust span { color: #FFD700; font-size: 14px; }
        .hero-image { flex: 1; }
        .hero-image img { width: 100%; border-radius: 10px; box-shadow: 0 20px 40px rgba(0,0,0,0.3); }
        
        /* Unclaimed Section */
        .unclaimed { background: #F5F0E6; padding: 40px 20px; border-top: 1px solid #FFD700; border-bottom: 1px solid #FFD700; }
        .unclaimed-inner { max-width: 1200px; margin: 0 auto; display: flex; justify-content: center; }
        .unclaimed-card { background: white; padding: 25px 40px; border-radius: 15px; display: flex; align-items: center; gap: 20px; cursor: pointer; transition: all 0.3s; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
        .unclaimed-card:hover { transform: scale(1.02); box-shadow: 0 8px 25px rgba(255,215,0,0.2); }
        .unclaimed-card img:first-child { height: 50px; }
        .unclaimed-text { display: flex; flex-direction: column; text-align: center; }
        .unclaimed-label { font-size: 14px; color: #FFD700; text-transform: uppercase; letter-spacing: 2px; }
        .unclaimed-number { font-size: 48px; font-weight: bold; color: #0A0A0A; line-height: 1; }
        .unclaimed-arrow { height: 30px; opacity: 0.7; }
        
        /* Features */
        .features { padding: 80px 20px; background: white; }
        .section-title { font-size: 40px; text-align: center; margin-bottom: 60px; color: #0A0A0A; }
        .section-title-white { font-size: 40px; text-align: center; margin-bottom: 20px; color: white; }
        .plans-subtitle { text-align: center; color: #FFD700; margin-bottom: 50px; font-size: 16px; }
        .features-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; max-width: 1200px; margin: 0 auto; }
        .feature-card { background: #F5F0E6; padding: 40px 30px; text-align: center; border-radius: 10px; transition: transform 0.3s; }
        .feature-card:hover { transform: translateY(-5px); }
        .feature-card img { height: 70px; margin-bottom: 20px; }
        .feature-card h3 { font-size: 20px; margin-bottom: 15px; }
        .feature-card p { color: #555; line-height: 1.6; }
        
        /* How It Works */
        .how-it-works { padding: 80px 20px; background: #F5F0E6; }
        .steps-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; max-width: 1200px; margin: 0 auto; text-align: center; }
        .step-number { width: 50px; height: 50px; background: #FFD700; color: #0A0A0A; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: bold; margin: 0 auto 20px; }
        .step-card img { width: 100%; height: 200px; object-fit: cover; border-radius: 10px; margin-bottom: 20px; }
        .step-card h3 { font-size: 20px; margin-bottom: 10px; }
        .step-card p { color: #666; }
        
        /* Plans */
        .plans { padding: 80px 20px; background: #0A0A0A; }
        .plans-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; max-width: 1200px; margin: 0 auto; }
        .plan-card { background: white; padding: 40px 25px; border-radius: 10px; text-align: center; position: relative; transition: transform 0.3s; }
        .plan-card:hover { transform: translateY(-5px); }
        .plan-card.popular { border: 2px solid #FFD700; transform: scale(1.02); }
        .popular-tag { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: #FFD700; color: #0A0A0A; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; white-space: nowrap; }
        .plan-icon { height: 70px; margin-bottom: 20px; }
        .plan-card h3 { font-size: 24px; margin-bottom: 15px; }
        .plan-price { font-size: 32px; font-weight: bold; color: #FFD700; margin-bottom: 25px; }
        .plan-price span { font-size: 12px; color: #666; display: block; }
        .plan-card ul { list-style: none; text-align: left; margin-bottom: 30px; }
        .plan-card ul li { font-size: 13px; padding: 6px 0; color: #333; }
        .plan-btn { display: block; width: 100%; padding: 12px; background: #0A0A0A; color: white; text-decoration: none; border-radius: 5px; font-weight: bold; transition: all 0.3s; }
        .plan-btn.gold { background: #FFD700; color: #0A0A0A; }
        .enterprise-note { text-align: center; margin-top: 50px; color: #ccc; font-size: 14px; }
        
        /* Testimonials with Videos */
        .testimonials { padding: 80px 20px; background: white; }
        .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; max-width: 1200px; margin: 0 auto; }
        .testimonial-card { background: #F5F0E6; padding: 20px; border-radius: 10px; }
        .testimonial-card.video-card { padding: 0; overflow: hidden; }
        .testimonial-video { width: 100%; border-radius: 10px 10px 0 0; }
        .testimonial-caption { padding: 15px; text-align: center; }
        .testimonial-caption h4 { font-size: 14px; margin-bottom: 3px; }
        .testimonial-caption p { font-size: 12px; color: #666; }
        .quote { height: 40px; opacity: 0.5; margin-bottom: 20px; }
        .testimonial-card p { font-size: 16px; font-style: italic; line-height: 1.6; margin-bottom: 20px; }
        .author { display: flex; align-items: center; gap: 15px; }
        .author img { width: 50px; height: 50px; border-radius: 50%; object-fit: cover; }
        .author h4 { font-size: 14px; margin-bottom: 3px; }
        .author p { font-size: 12px; color: #666; margin: 0; font-style: normal; }
        
        /* Final CTA */
        .final-cta { padding: 80px 20px; background: #F5F0E6; text-align: center; }
        .final-cta-content { max-width: 800px; margin: 0 auto; }
        .final-cta-content h2 { font-size: 36px; color: #0A0A0A; margin-bottom: 15px; }
        .final-cta-content p { font-size: 18px; color: #555; margin-bottom: 30px; }
        .final-cta-buttons { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; }
        .final-cta-primary { padding: 14px 32px; background: #FFD700; color: #0A0A0A; text-decoration: none; border-radius: 5px; font-weight: bold; transition: all 0.3s; }
        .final-cta-primary:hover { background: #B8860B; }
        .final-cta-secondary { padding: 14px 32px; background: transparent; border: 2px solid #0A0A0A; color: #0A0A0A; text-decoration: none; border-radius: 5px; font-weight: bold; transition: all 0.3s; }
        .final-cta-secondary:hover { background: #0A0A0A; color: white; }
        
        /* Video Modal */
        .video-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.9); display: flex; align-items: center; justify-content: center; z-index: 2000; }
        .video-modal-content { position: relative; width: 90%; max-width: 1000px; }
        .video-modal-close { position: absolute; top: -40px; right: 0; background: none; border: none; color: white; font-size: 32px; cursor: pointer; }
        .explainer-video { width: 100%; border-radius: 10px; }
        
        /* Modal */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background: white; padding: 40px; border-radius: 15px; max-width: 500px; width: 90%; text-align: center; position: relative; }
        .modal-close { position: absolute; top: 15px; right: 15px; background: none; border: none; font-size: 24px; cursor: pointer; }
        .modal-content img { height: 60px; margin-bottom: 20px; }
        .modal-content h2 { color: #FFD700; margin-bottom: 20px; }
        .modal-info { background: #F5F0E6; padding: 20px; border-radius: 10px; text-align: left; margin: 20px 0; }
        .modal-btn { width: 100%; padding: 12px; background: #FFD700; border: none; border-radius: 5px; font-weight: bold; cursor: pointer; }
        
        /* Responsive */
        @media (max-width: 1024px) {
          .features-grid, .plans-grid, .steps-grid, .testimonials-grid { grid-template-columns: repeat(2, 1fr); }
          .hero-title { font-size: 40px; }
        }
        
        @media (max-width: 768px) {
          .hero-inner { flex-direction: column; padding: 50px 20px; text-align: center; }
          .hero-title { font-size: 32px; }
          .hero-buttons { justify-content: center; }
          .hero-trust { margin: 0 auto; }
          .features-grid, .plans-grid, .steps-grid, .testimonials-grid { grid-template-columns: 1fr; }
          .section-title, .section-title-white { font-size: 28px; }
          .plan-card.popular { transform: scale(1); }
          .unclaimed-card { flex-direction: column; text-align: center; padding: 20px; gap: 15px; }
          .final-cta-content h2 { font-size: 28px; }
          .final-cta-buttons { flex-direction: column; align-items: center; }
        }
        
      `}} />
      
    </div>
  );
};

export default Landing;